// The one place role names are matched.
//
// Role checks used to be re-implemented in each component that needed one, and
// the copies drifted: device.vue's `isAdmin` accepted only ADMIN while the store
// getter, nav-bar and main.vue all accepted ADMIN or DEVELOPER. So a DEVELOPER
// passed the router's `adminRequired` gate and then lost that page's admin
// actions. Everything now funnels through here (see the `auth` module's
// isAdmin / isAdminOrAgent / isDeveloper getters, and the router's meta-driven
// role gates), so a change lands everywhere at once.

// Role entries arrive either as `{ name }` objects (the /me payload) or as plain
// strings (a persisted userData blob, and the CLIENT fallback in auth.js), so
// every check normalizes first. Anything unusable is dropped rather than throwing.
export function roleNames(roles) {
  if (!Array.isArray(roles)) return []
  return roles
    .map((role) => (typeof role === 'string' ? role : role && role.name))
    .filter(Boolean)
}

// True when `roles` contains any of `names`. Always returns a boolean, so it can
// be bound straight to a `v-if` without leaking undefined.
export function hasAnyRole(roles, ...names) {
  const held = roleNames(roles)
  return names.some((name) => held.includes(name))
}

// The roles BowVue comes with, in the order the roles screen lists them. The
// API's assignableRoles adds the ones added from the Role Permissions page
// after these; this list is what is used until it answers.
//
// A role a user still holds that is NOT listed here is a legacy assignment and
// must never be presented as settable. SUPER is the case that matters: backend
// migration 00031 retired it in favour of DEVELOPER, but existing rows in
// role_users outlive the code, so the roles screen was still rendering a live
// SUPER toggle for anyone who had it.
export const GRANTABLE_ROLES = [
  'DEVELOPER',
  'ADMIN',
  'AGENT',
  'MANAGER',
  'CLIENT',
  // Scheduling only: a GUEST exists so they can be put on the board, and the
  // API refuses their token. Granting them any other role is what lets them
  // into BowVue. And sends the invite email their creation withheld.
  'GUEST',
]

// The switch list for one user: every grantable role (the API's
// assignableRoles when given, else GRANTABLE_ROLES), flagged with whether they
// currently hold it. Derived from that list rather than from what the user
// holds, which is precisely what keeps a retired role off the screen.
export function roleToggles(held, grantable) {
  const names = roleNames(held)
  const list =
    Array.isArray(grantable) && grantable.length ? grantable : GRANTABLE_ROLES
  return list.map((role) => ({
    role,
    active: names.includes(role),
  }))
}

// Whether an address is the company's own (case and surrounding spaces
// ignored, as the API decides it: models.IsBowtieEmail). An account outside it
// starts as a Client when created (2026-10-19).
export function isBowtieEmail(email) {
  return String(email || '')
    .trim()
    .toLowerCase()
    .endsWith('@bowtie8.com')
}
